<template>
  <div id="header" class="header" :class="[loaderActivityClass, loaderTransitionClass]">
    <div
      id="client-logo"
      class="client-logo"
      v-if="clientLogo && clientLogo !== ''"
      :style="`background-image: url(${clientLogo}`"
    ></div>
    <div class="product-info">
      <div class="product-logo middle">
        <div v-if="gameLogo" v-html="gameLogo"></div>
      </div>
      <div class="product-name-wrap middle">
        <div class="product-name">{{ title }}</div>
      </div>
    </div>
    <div class="round-info retail-view middle">
      <div v-if="roundInfo && roundInfo.active" class="current-round-info round-info-item">
        <span id="round-id" class="round-info-number round-id middle">{{ eventId }}</span>
        <span class="round-info-text middle">{{ roundInfo.text }}</span>
      </div>
      <div v-else-if="nextRoundInfo && nextRoundInfo.active" class="next-round-info round-info-item">
        <span id="remaining-time" class="round-info-number remaining-time middle">{{ formattedCountdownTime }}</span>
        <span class="round-info-text middle">{{ nextRoundInfo.text }}</span>
      </div>
      <div v-else-if="specialInfo && specialInfo.active" class="special-info round-info-item">
        <span class="round-info-text middle">{{ specialInfo.text }}</span>
      </div>
    </div>
    <app-loader
      :title="title"
      :infoMessage="infoMessage"
      :countdownTime="countdownTime"
      :countdownActive="loaderCountdownActive"
      :progressStartValue="progressStartValue"
      :progressFullWidth="progressFullWidth"
      :loaderTransitionDuration="loaderTransitionDuration"
    ></app-loader>
  </div>
</template>

<script>
import { formatTime } from '../utility';
import AppLoader from './AppLoader';

export default {
  name: 'AppHeader',
  components: { AppLoader },
  props: {
    // Product name (Pascal case, e.g. VirtualDragRaces)
    name: {
      type: String,
      default: '',
    },
    // Product title (space separator, e.g. Virtual Drag Races)
    title: {
      type: String,
      default: '',
    },
    // Info message displayed on loader screen
    infoMessage: {
      type: String,
      default: '',
    },
    // Current round or event number
    eventId: {
      type: [Number, String],
      default: '-',
    },
    // Source path for client logo if provided
    clientLogo: {
      type: [Boolean, String],
      default: '',
    },
    // Countdown time till next round
    countdownTime: {
      type: Number,
      default: 0,
    },
    // Have countdown time in seconds presented like min:sec (83 -> 1:23)
    formatCountdownTime: {
      type: Boolean,
      default: true,
    },
    // Toogle state for displaying loader
    loaderActive: {
      type: Boolean,
      default: false,
    },
    // Start time for progressbar animation
    progressStartValue: {
      type: Number,
      default: 0,
    },
    // Force full width of progressbar
    progressFullWidth: {
      type: Boolean,
      default: true,
    },
    // Control visibility of round info inside header
    roundInfo: {
      type: Object,
      default() {
        return {
          active: false,
          text: 'Round number',
        };
      },
    },
    // Control visibility of next round info inside header
    nextRoundInfo: {
      type: Object,
      default() {
        return {
          active: false,
          text: 'Betting time left',
        };
      },
    },
    // Control visibility of special info inside header
    specialInfo: {
      type: Object,
      default() {
        return {
          active: false,
          text: 'Last bets please!',
        };
      },
    },
    // Control loader transition effects
    animationSet: {
      type: String,
      default: 'full',
    },
    // Display countdown on loader screen or not
    loaderCountdownActive: {
      type: Boolean,
      default: false,
    },
    // Duration of loader animation in miliseconds
    loaderTransitionDuration: {
      type: Number,
      default: 1000,
    },
  },
  data() {
    return {
      animationSets: ['full', 'basic', 'none'],
      gameLogo: null,
    };
  },
  computed: {
    loaderActivityClass() {
      return this.loaderActive ? 'loader-on' : 'loader-off';
    },
    loaderTransitionClass() {
      const set = this.animationSets.includes(this.animationSet) ? this.animationSet : 'full';

      return `animations-${set}`;
    },
    formattedCountdownTime() {
      return this.formatCountdownTime ? formatTime(this.countdownTime) : this.countdownTime;
    },
  },
  methods: {
    setGameLogo() {
      try {
        this.gameLogo = require(`!html-loader!../assets/game-logo_${this.name}.svg`);
      } catch (err) {
        this.gameLogo = require('!html-loader!../assets/game-logo_NSoft.svg');
      }
    },
  },
  created() {
    this.setGameLogo();
  },
};
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css?family=Roboto:400,700|Nunito:400,700&amp;subset=cyrillic,cyrillic-ext,latin-ext');
.header {
  position: relative;
  width: 100%;
  height: $header-loader-height-sm;
  bottom: $progressbar-height-sm;
  text-transform: uppercase;
  backface-visibility: hidden;
  color: #fff;
  z-index: 98;

  & .retail-view {
    display: inherit;
  }

  .client-logo {
    position: absolute;
    top: 11px;
    left: 1%;
    width: 175px;
    height: 25px;
    background: {
      repeat: no-repeat;
      position: center left;
      size: contain;
    }
    transition: opacity 0.75s 0.5s;
  }

  .product-info {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    width: 100%;
    height: 100%;
    line-height: $header-height-sm;
    margin: 0 auto;
    vertical-align: middle;
    text-align: center;
    animation: fadeIn 0.5s forwards;
    backface-visibility: hidden;
    z-index: 0;
  }

  .product-logo {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    display: inline-block;
    width: 34px;
    height: 34px;
    margin: 0 2px;
    vertical-align: middle;
    transition: all 0.65s ease-in-out;
    backface-visibility: hidden;

    .inner {
      transition: all 0.4s;
    }

    div {
      position: relative;
      top: -1px;
      height: 100%;
    }

    svg {
      width: 100%;
      // height: 100%;
    }
  }

  .product-name-wrap {
    display: none;
    max-width: 190px;
    width: auto;
    backface-visibility: hidden;
    opacity: 0;
  }

  .product-name {
    position: relative;
    display: inline-block;
    width: auto;
    line-height: 1;
    color: #f0f0f0;
    text-align: left;
    font: {
      family: 'Nunito', sans-serif;
      size: 12px;
      weight: 700;
    }
    vertical-align: middle;
    backface-visibility: hidden;
  }

  .round-info {
    transition: opacity 0.75s 0.5s;
    font-family: 'Roboto';
  }

  .round-info-item {
    margin-right: 1%;
  }

  .round-info-number {
    float: right;
    margin: 0.3% 0 auto 0.7%;
    color: rgba(255, 255, 255, 0.6);
    font-size: 26px;
  }

  .round-info-text {
    float: right;
    line-height: 1.2;
    max-width: 65px;
    margin-top: 0.7%;
    color: rgba(255, 255, 255, 0.6);
    font-size: 10px;
    text-align: right;

    & em:first-child {
      display: block;
      float: right;
    }

    .round-id {
      font-weight: 700;
      margin: 0 3px;
    }
  }
}

.animations-basic {
  .product-info {
    animation: loaderOnProductInfo 0s forwards;
  }
}

.stream-runner-mode {
  .header {
    height: $header-loader-height-xl;
    .product-info {
      align-items: flex-start;
      padding-top: 3px;
    }
    .product-logo {
      width: 55px;
      height: 53px;
    }
    .product-name-wrap {
      max-width: 290px;
      width: auto;
    }
    .product-name {
      font-size: 19px;
    }
    .round-info-number {
      font-size: 43px;
    }
    .round-info-text {
      max-width: 110px;
      line-height: 1;
      font-size: 18px;

      .round-id {
        font-size: 1.2em;
      }
    }
  }
}

@media screen and (min-width: $breakpoint-mobile) {
  .header {
    height: $header-loader-height-md;

    .product-info {
      line-height: $header-height-md;
    }

    .product-name-wrap {
      display: inline-block;
    }

    .product-logo {
      width: 40px;
      height: 42px;

      div {
        top: 0;
      }
    }

    .round-info-number {
      margin: 0.1% 0 auto 0.7%;
      font-size: 28px;
    }

    .round-info-text {
      margin-top: 0.1%;
      font-size: 11px;
    }
  }
}

@media screen and (min-width: $breakpoint-web) {
  .header {
    height: get-vw($header-loader-height-md, $viewport-width-web);

    .client-logo {
      top: get-vw(11px, $viewport-width-web);
      width: get-vw(175px, $viewport-width-web);
      height: get-vw(25px, $viewport-width-web);
    }

    .product-info {
      line-height: get-vw($header-height-md, $viewport-width-web);
    }

    .product-logo {
      width: get-vw(40px, $viewport-width-web);
      height: get-vw(42px, $viewport-width-web);
    }

    .product-name-wrap {
      max-width: get-vw(190px, $viewport-width-web);
    }

    .product-name {
      font-size: get-vw(12px, $viewport-width-web);
    }

    .round-info-number {
      font-size: get-vw(28px, $viewport-width-web);
    }

    .round-info-text {
      max-width: get-vw(65px, $viewport-width-web);
      font-size: get-vw(11px, $viewport-width-web);
    }
  }
}

@media screen and (min-width: $breakpoint-desktop) {
  .header {
    height: get-vw($header-loader-height);
    bottom: $progressbar-height;

    .client-logo {
      top: get-vw(11px);
      width: get-vw(200px);
      height: get-vw(35px);
    }

    .product-info {
      line-height: get-vw($header-height);
    }

    .product-logo {
      width: get-vw(44px);
      height: get-vw(47px);
      margin: 0 get-vw(2px);
    }

    .product-name-wrap {
      max-width: get-vw(290px);
    }

    .product-name {
      font-size: get-vw(16px);
    }

    .round-info-number {
      font-size: get-vw(28px);
    }

    .round-info-text {
      max-width: get-vw(70px);
      font-size: get-vw(11px);
    }
  }
}

@media screen and (min-width: $breakpoint-desktop-max) {
  .header {
    height: get-vw($header-loader-height-max, $viewport-width-max);
    bottom: $progressbar-height-max;

    .product-info {
      line-height: get-vw($header-height-max, $viewport-width-max);
    }
  }
}

.animations-none {
  .product-logo {
    transition: opacity 0s 0s, transform 0.3s 0.3s ease-in-out;
  }
}

.loader-on {
  &.header {
    .client-logo {
      opacity: 0 !important;
    }

    .product-logo {
      transform: translateY(33vh) scale(1.7);

      .inner {
        fill: #fff;
        opacity: 0.1;
        transition-delay: 0.75s;
      }
    }

    .product-name-wrap {
      max-width: 0;
      width: auto;
      transition: opacity 0.1s, max-width 0.6s ease-out;
      transition-delay: 0s;
    }

    .product-name {
      color: transparent;
    }

    .round-info {
      opacity: 0;
      transition-delay: 0s;
    }

    @media screen and (min-width: $breakpoint-mobile-md) {
      .product-logo {
        transform: translateY(32vh) scale(2.3);
      }
    }

    @media screen and (min-width: $breakpoint-mobile) {
      .product-logo {
        transform: translateY(30vh) scale(2);
      }
    }

    @media screen and (min-width: $breakpoint-web-md) {
      .product-logo {
        transform: translateY(32vh) scale(2.4);
      }
    }

    @media screen and (min-width: $breakpoint-web) {
      .product-logo {
        transform: translateY(32vh) scale(3);
      }
    }

    @media screen and (min-width: $breakpoint-desktop-sm) {
      .product-logo {
        transform: translateY(33vh) scale(3.5);
      }
    }

    @media screen and (min-width: $breakpoint-desktop) {
      .product-logo {
        transform: translateY(33vh) scale(3.8);
      }
    }
  }

  &.animations-basic {
    .product-info {
      animation: loaderOnProductInfo 1s forwards;
    }
    .product-logo {
      transition-duration: 0s;
      transition-delay: 0.2s;
    }
  }

  &.animations-none {
    .product-logo,
    .product-name-wrap {
      transition-duration: 0s;
      transition-delay: 0s;
    }
  }
}

@keyframes loaderOnProductInfo {
  0% {
    opacity: 1;
  }
  20% {
    opacity: 0;
  }
  60% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@keyframes loaderOffProductInfo {
  0% {
    opacity: 1;
  }
  20% {
    opacity: 0;
  }
  80% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.stream-runner-mode .loader-on .product-logo {
  transform: translateY(32vh) scale(3.4);
}

.loader-off {
  &.header {
    .product-logo {
      transition-duration: 0.6s;
      transition-timing-function: ease-out;
      transition-delay: 0.2s;
    }

    @media screen and (min-width: $breakpoint-mobile) {
      .product-name-wrap {
        opacity: 1;
        transition: opacity 0.2s 1s, max-width 0.75s 0.25s ease-in;
      }
    }
  }

  &.animations-basic {
    .product-info {
      animation: loaderOffProductInfo 1s forwards;
    }
    .product-logo {
      transition-duration: 0s;
      transition-delay: 0.4s;
    }
    .product-name-wrap {
      transition: opacity 0.5s 0.9s, max-width 0s 0.2s;
    }
  }

  &.animations-none {
    .product-logo {
      transition-delay: 0s;
      transition-duration: 0s;
    }
    .product-name-wrap {
      transition: all 0s, opacity 0.6s 0.5s;
      transition-delay: 0s;
    }
  }
}
</style>
// SVG logo style
<style lang="scss">
.product-logo svg {
  width: 100%;
}
.outer-part {
  fill: #fff;
  opacity: 0.25;
}
.inner-part {
  fill: #fff;
  opacity: 0.6;
  transition: all 0.4s;

  &.highlight {
    fill: #fff;
    opacity: 1;
  }
}
.loader-on {
  .inner-part {
    fill: #fff;
    opacity: 0.25;
    transition-delay: 0.75s;
  }

  &.animations-none {
    .inner-part {
      transition-delay: 0.1s;
    }
  }
}
.loader-off {
  &.animations-basic {
    .inner-part {
      transition-delay: 0.9s;
    }
  }
  &.animations-none {
    .inner-part {
      transition-delay: 0.1s;
    }
  }
}
</style>
